import {useContext, useEffect, useMemo, useState} from 'react';
import {ChoiceSelect} from '@/components/app/choice-select';
import {Button} from '@/components/app/button';
import {Hierarchy} from '@/components/app/hierarchy';
import {cn} from '@/lib/utils';
import {Icon} from '../../../app/components/Icon';
import {PersonContext} from '../../person/contexts/PersonContext';
import {RelevantCondition} from '../types';
import {ToolDetailsDialog} from './ToolDetailsDialog';
interface TreeNode {
  children: TreeNode[];
  data: {
    ascendents: RelevantCondition[];
    relevantCondition: RelevantCondition;
    selection: 'single' | 'multiple';
  };
  key: number;
  label: string;
  leaf: boolean;
}
interface SettingSelectionPanelProps {
  className?: string;
  onSave: (event: {deselectedIds: number[]; selectedIds: number[]}) => void;
  onSelectGroup?: (event: {conditionId: number}) => void;
  relevantCondition: RelevantCondition;
}
export const SettingSelectionPanel = ({
  className,
  onSave,
  onSelectGroup,
  relevantCondition,
}: SettingSelectionPanelProps) => {
  const {contains, isRequiredRelated, isSelected, relationsByConditionId, relevantConditionsDeduplicated} =
    useContext(PersonContext);
  const [selectedGroupId, setSelectedGroupId] = useState<number>();
  const [willBeDeselectedConditionIds, setWillBeDeselectedConditionIds] = useState<number[]>([]);
  const [willBeSelectedConditionIds, setWillBeSelectedConditionIds] = useState<number[]>([]);
  const [toolDetailsDialogVisible, setToolDetailsDialogVisible] = useState(false);
  const [toolDetailsLabelId, setToolDetailsLabelId] = useState<number>();
  const treeNodes = useMemo(() => createTreeNodes(relevantCondition), [relevantCondition]);
  const treeValue = treeValueFactory(treeNodes, selectedGroupId);
  const treeExpandedKeys = useMemo(
    () => Object.fromEntries(relevantConditionsDeduplicated?.map((rc) => [rc.conditionId, true]) ?? []),
    [relevantConditionsDeduplicated],
  );
  useEffect(() => {
    setWillBeDeselectedConditionIds([]);
    setWillBeSelectedConditionIds([]);
  }, [relevantCondition, relevantConditionsDeduplicated]);
  const changesExist = hasChanges();
  return (
    <>
      <div className={cn('flex flex-col bg-gray-100 sm:rounded-xl', className)}>
        <div
          className="flex items-center p-6"
          style={
            changesExist
              ? {
                  background: 'inherit',
                  borderRadius: 'inherit',
                  position: 'sticky',
                  top: '2.25rem',
                  zIndex: 1,
                }
              : {}
          }>
          <div className="text-blue-400">Select settings in {relevantCondition.conditionName}</div>
          {changesExist && (
            <div
              className="flex items-center ml-auto"
              style={{
                height: 0,
              }}>
              <Button
                onClick={() => cancelChanges()}
                type={'button'}
                variant="ghost"
                className="rounded-full"
                startIcon={<Icon icon={'times'} />}>
                {'Cancel Changes'}
              </Button>
              <Button
                onClick={() => saveChanges()}
                type={'button'}
                variant="success-ghost"
                className="rounded-full"
                startIcon={<Icon icon={'save'} />}>
                {'Save Changes'}
              </Button>
            </div>
          )}
        </div>
        <div className={'lg:flex flex-1 min-h-0 px-6 pb-6'}>
          <ChoiceSelect
            className={'lg:hidden w-full mb-4'}
            itemTemplate={(option) => <div className={'pl-4 text-foreground'}>{option.label}</div>}
            onValueChange={(e) => selectGroup(e)}
            optionGroupChildren={'items'}
            optionGroupLabel={'label'}
            optionGroupTemplate={(option) => <div className={'font-semibold text-foreground'}>{option.label}</div>}
            options={relationsByConditionId[relevantCondition.conditionId].optionalRelatedList.flatMap((rc) => {
              const childList = relationsByConditionId[rc.conditionId].childList;
              const groupChildList = childList.filter((child) => child.isGroup);
              const nonGroupChildList = childList.filter((child) => !child.isGroup);
              return {
                label: rc.conditionName,
                items: [
                  ...groupChildList.map((child) => ({
                    label: child.conditionName,
                    value: child.conditionId,
                    visible: true,
                  })),
                  {
                    label: groupChildList.length > 0 ? 'Others' : rc.conditionName.replace(', related', ''),
                    value: rc.conditionId,
                    visible: nonGroupChildList.length > 0,
                  },
                ].filter((item) => item.visible !== false),
              };
            })}
            placeholder={'Select a group'}
            value={selectedGroupId}
          />
          <div className={'hidden lg:block flex-1 min-w-0 overflow-auto mb-4'}>
            {relationsByConditionId[relevantCondition.conditionId].optionalRelatedList.map((rc) => {
              const childList = relationsByConditionId[rc.conditionId].childList;
              const groupChildList = childList.filter((child) => child.isGroup);
              const nonGroupChildList = childList.filter((child) => !child.isGroup);
              return (
                <section key={rc.conditionId}>
                  <header>
                    <h6 className={'text-base font-semibold my-0 py-2 pr-4'}>{rc.conditionName}</h6>
                  </header>
                  <div>
                    {groupChildList.map((child) => {
                      const selected = selectedGroupId === child.conditionId;
                      return (
                        <div
                          className={cn(
                            'cursor-pointer px-4 py-2 rounded-l-xl',
                            selected ? 'bg-white text-primary' : 'hover:bg-gray-200',
                          )}
                          onClick={() => selectGroup(child.conditionId)}
                          onKeyDown={(e) => e.key === 'Enter' && selectGroup(child.conditionId)}
                          key={child.conditionId}
                          role={'button'}
                          tabIndex={0}>
                          {child.conditionName}
                        </div>
                      );
                    })}
                    {nonGroupChildList.length > 0 && (
                      <div
                        className={cn(
                          'cursor-pointer px-4 py-2 rounded-l-xl',
                          selectedGroupId === rc.conditionId ? 'bg-white text-primary' : 'hover:bg-gray-200',
                        )}
                        onClick={() => selectGroup(rc.conditionId)}
                        onKeyDown={(e) => e.key === 'Enter' && selectGroup(rc.conditionId)}
                        role={'button'}
                        tabIndex={0}>
                        {groupChildList.length > 0 ? 'Others' : rc.conditionName.replace(', related', '')}
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
          <div className={'flex-[2] min-w-0 p-4 overflow-auto bg-white rounded-xl'}>
            {treeValue.length > 0 && (
              <Hierarchy
                expanded={treeExpandedKeys}
                renderNode={(node) => {
                  if (node.data == null) {
                    return null;
                  }
                  const rc = node.data.relevantCondition as RelevantCondition;
                  if (rc.isGroup) {
                    return <h6 className={'p-2 font-medium text-base my-0'}>{rc.conditionName}</h6>;
                  }
                  const requiredRelated = isRequiredRelated(rc.conditionId);
                  const selected = isSelected(rc.conditionId);
                  const selectedDescendentIds = getSelectedDescendentIds(rc);
                  const willBeDeselected = willBeDeselectedConditionIds.includes(rc.conditionId);
                  const willBeDeselectedDescendentIds = getWillBeDeselectedDescendentIds(rc);
                  const willBeSelected = willBeSelectedConditionIds.includes(rc.conditionId);
                  const willBeSelectedDescendentIds = getWillBeSelectedDescendentIds(rc);
                  const willBeChanged = willBeDeselected || willBeSelected;
                  const willBeEmpty =
                    !selected &&
                    selectedDescendentIds.length > 0 &&
                    selectedDescendentIds.length === willBeDeselectedDescendentIds.length &&
                    willBeSelectedDescendentIds.length === 0;
                  const disabled =
                    requiredRelated ||
                    (selectedDescendentIds.length > 0 &&
                      willBeDeselectedDescendentIds.length < selectedDescendentIds.length) ||
                    willBeSelectedDescendentIds.length > 0;
                  const selection = node.data.selection;
                  return (
                    <div
                      aria-disabled={disabled}
                      className={cn('flex items-center gap-2 w-full p-2 rounded-xl', {
                        'opacity-50': disabled,
                        'cursor-pointer hover:bg-gray-200': !disabled,
                        'text-yellow-800': willBeChanged || willBeEmpty,
                      })}
                      onClick={() => {
                        if (!disabled) {
                          if (rc.isTool) {
                            setToolDetailsDialogVisible(true);
                            setToolDetailsLabelId(rc.conditionId);
                          } else {
                            toggleConditionSelection(rc);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        e.key === 'Enter' && !disabled && !rc.isTool && toggleConditionSelection(rc);
                      }}
                      role={'button'}
                      tabIndex={0}>
                      {!rc.isTool && (
                        <Icon
                          className={'block'}
                          icon={
                            willBeDeselected || (!willBeSelected && willBeEmpty)
                              ? selection === 'multiple'
                                ? 'stop'
                                : 'circle'
                              : willBeSelected || willBeSelectedDescendentIds.length > 0
                                ? selection === 'multiple'
                                  ? 'check-square'
                                  : 'check-circle'
                                : requiredRelated || selected || selectedDescendentIds.length > 0
                                  ? selection === 'multiple'
                                    ? 'check-square'
                                    : 'check-circle'
                                  : selection === 'multiple'
                                    ? 'stop'
                                    : 'circle'
                          }
                          style={{
                            marginTop: -1,
                          }}
                        />
                      )}
                      {rc.isTool && (
                        <Icon
                          className={'block'}
                          icon={'wrench'}
                          style={{
                            marginTop: -1,
                          }}
                        />
                      )}
                      <div className={'flex-1 min-w-0 whitespace-nowrap text-ellipsis overflow-hidden'}>
                        {rc.conditionName}
                      </div>
                    </div>
                  );
                }}
                nodes={treeValue}
              />
            )}
            {selectedGroupId != null && treeValue.length === 0 && (
              <div className={'text-muted-foreground p-2'}>No settings available.</div>
            )}
            {selectedGroupId == null && (
              <div className={'text-muted-foreground p-2'}>Please select a group to list settings.</div>
            )}
          </div>
        </div>
      </div>
      <ToolDetailsDialog
        labelId={toolDetailsLabelId}
        onClose={() => setToolDetailsDialogVisible(false)}
        open={toolDetailsDialogVisible}
      />
    </>
  );
  function selectGroup(conditionId: number) {
    setSelectedGroupId(conditionId);
    onSelectGroup?.({
      conditionId,
    });
  }
  function treeValueFactory(nodes: TreeNode[], selectedId: number | undefined) {
    if (selectedId == null) {
      return [];
    }
    const flatNodes = nodes.flatMap((node) => [node, ...node.children]);
    const selectedNode = flatNodes.find((node) => node.data.relevantCondition.conditionId === selectedId);
    if (selectedNode != null && nodes.includes(selectedNode)) {
      return selectedNode.children.filter((child) => !child.data.relevantCondition.isGroup);
    }
    return selectedNode?.children ?? [];
  }
  function createTreeNodes(relevantCondition: RelevantCondition): TreeNode[] {
    return relationsByConditionId[relevantCondition.conditionId].optionalRelatedList.map((rc) => createTreeNode(rc));
  }
  function createTreeNode(relevantCondition: RelevantCondition): TreeNode {
    const childList = relationsByConditionId[relevantCondition.conditionId].childList;
    const ascendents =
      relevantConditionsDeduplicated?.filter(
        (rc) =>
          rc.conditionId != relevantCondition.conditionId && contains(rc.conditionId, relevantCondition.conditionId),
      ) ?? [];
    return {
      children: childList
        .slice()
        .sort(compareRelevantConditions)
        .map((child) => createTreeNode(child)),
      data: {
        ascendents,
        relevantCondition,
        selection: ascendents.some((a) => a.isChildSelectionRestricted) ? 'single' : 'multiple',
      },
      key: relevantCondition.conditionId,
      label: relevantCondition.conditionName,
      leaf: childList.length === 0,
    };
  }
  function cancelChanges() {
    setWillBeDeselectedConditionIds([]);
    setWillBeSelectedConditionIds([]);
  }
  function hasChanges() {
    return willBeDeselectedConditionIds.length > 0 || willBeSelectedConditionIds.length > 0;
  }
  function saveChanges() {
    onSave({
      deselectedIds: willBeDeselectedConditionIds,
      selectedIds: willBeSelectedConditionIds,
    });
  }
  function getSelectedDescendentIds(relevantCondition: RelevantCondition): number[] {
    const {childList} = relationsByConditionId[relevantCondition.conditionId];
    return childList.flatMap((child) =>
      isSelected(child.conditionId) ? child.conditionId : getSelectedDescendentIds(child),
    );
  }
  function getWillBeDeselectedDescendentIds(relevantCondition: RelevantCondition): number[] {
    const {childList} = relationsByConditionId[relevantCondition.conditionId];
    return childList.flatMap((child) =>
      willBeDeselectedConditionIds.includes(child.conditionId)
        ? child.conditionId
        : getWillBeDeselectedDescendentIds(child),
    );
  }
  function getWillBeSelectedDescendentIds(relevantCondition: RelevantCondition): number[] {
    const {childList} = relationsByConditionId[relevantCondition.conditionId];
    return childList.flatMap((child) =>
      willBeSelectedConditionIds.includes(child.conditionId)
        ? child.conditionId
        : getWillBeSelectedDescendentIds(child),
    );
  }
  function toggleConditionSelection(rc: RelevantCondition) {
    const selected = isSelected(rc.conditionId);
    const requiredRelated = isRequiredRelated(rc.conditionId);
    const willBeSelected = willBeSelectedConditionIds.includes(rc.conditionId);
    const willBeDeselected = willBeDeselectedConditionIds.includes(rc.conditionId);
    const ascendents = relevantConditionsDeduplicated?.filter((rc2) => contains(rc2.conditionId, rc.conditionId)) ?? [];
    const ascendentIds = ascendents.map((rc2) => rc2.conditionId) ?? [];
    const childSelectionRestrictedAscendent = ascendents.find((ascendent) => ascendent.isChildSelectionRestricted);
    const childSelectionRestricted = childSelectionRestrictedAscendent != null;
    const excludedIds =
      relevantConditionsDeduplicated
        ?.filter((rc2) => rc2.conditionId !== rc.conditionId)
        .filter(
          (rc2) =>
            ascendentIds.includes(rc2.conditionId) ||
            ascendentIds
              .filter(
                (ascendentId) =>
                  childSelectionRestrictedAscendent == null ||
                  childSelectionRestrictedAscendent.conditionId === ascendentId ||
                  contains(childSelectionRestrictedAscendent.conditionId, ascendentId),
              )
              .some((ascendentId) => contains(ascendentId, rc2.conditionId)),
        )
        .map((rc) => rc.conditionId) ?? [];
    if (selected || requiredRelated) {
      if (willBeDeselected) {
        setWillBeDeselectedConditionIds((ids) => ids.filter((id) => id !== rc.conditionId));
        if (childSelectionRestricted) {
          setWillBeSelectedConditionIds((ids) => ids.filter((id) => !excludedIds.includes(id)));
        }
      } else {
        setWillBeDeselectedConditionIds((ids) => [...ids, rc.conditionId]);
      }
    }
    if (!selected && !requiredRelated) {
      if (willBeSelected) {
        setWillBeSelectedConditionIds((ids) => ids.filter((id) => id !== rc.conditionId));
      } else {
        if (childSelectionRestricted) {
          setWillBeSelectedConditionIds((ids) => [...ids.filter((id) => !excludedIds.includes(id)), rc.conditionId]);
          setWillBeDeselectedConditionIds((ids) =>
            Array.from(new Set([...ids, ...excludedIds.filter((id) => isSelected(id))])),
          );
        } else {
          setWillBeSelectedConditionIds((ids) => [...ids.filter((id) => !ascendentIds.includes(id)), rc.conditionId]);
        }
      }
    }
  }
};
function compareRelevantConditions(a: RelevantCondition, b: RelevantCondition) {
  return (
    Number(a.showOrder ?? Number.MAX_SAFE_INTEGER) - Number(b.showOrder ?? Number.MAX_SAFE_INTEGER) ||
    a.conditionName.localeCompare(b.conditionName)
  );
}
